import jsPDF from 'jspdf';
import { drawWatermark, drawHeader, drawFooter, LOGO_URL, loadImageAsDataURL, drawCTAButtons, CTA_BLOCK_HEIGHT, FOOTER_RESERVED_HEIGHT, drawNearbyMillsDistance, shortName } from '../../market-intelligence/shareExport';
import { fmtNum, fetchDistrictMillDistances } from '../../market-intelligence/utils';

const formatDatePDF = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMonthPDF = (periodMonth) => {
  if (!periodMonth) return null;
  const d = new Date(periodMonth);
  if (isNaN(d?.getTime())) return null;
  return d?.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

// Real jsPDF export for a single mill's rate snapshot — replaces the old window.print() approach.
// Reuses drawHeader/drawWatermark/drawFooter/LOGO_URL from market-intelligence/shareExport.js (now
// exported from there) instead of duplicating the brand drawing logic, so this page can never visually
// drift from the rest of SugarHathi's PDF exports.
export async function generateFactoryRatePDF({ card, managers, liveRates, chartRef, action = 'download' }) {
  const logoDataUrl = await loadImageAsDataURL(LOGO_URL);
  const w = 595, h = 842; // A4 in points — matches every other SugarHathi export
  const doc = new jsPDF({ unit: 'pt', format: [w, h] });
  const marginX = 36;
  const contentW = w - marginX * 2;

  let chartImg = null;
  let chartAspect = 3.2;
  if (chartRef?.current) {
    try {
      const html2canvas = (await import('html2canvas'))?.default;
      const canvas = await html2canvas(chartRef?.current, { backgroundColor: '#FFFFFF', scale: 2, useCORS: true });
      chartImg = canvas?.toDataURL('image/png');
      chartAspect = canvas?.width / canvas?.height;
    } catch (e) {
      chartImg = null; // MI panel snapshot missing shouldn't block the PDF
    }
  }

  drawWatermark(doc, w, h);
  drawHeader(doc, w, 'Factory Rate Intelligence Center · Rate Snapshot', logoDataUrl, 'SugarHathi Rate Snapshot');

  const today = new Date()?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  doc?.setFontSize(8);
  doc?.setTextColor(102, 102, 102);
  doc?.setFont(undefined, 'normal');
  doc?.text(`GENERATED ${today?.toUpperCase()}`, marginX, 62);

  // ── Factory identity ──
  doc?.setFontSize(15);
  doc?.setTextColor(17, 17, 17);
  doc?.setFont(undefined, 'bold');
  const nameLines = doc?.splitTextToSize(card?.name || '—', contentW);
  let y = 82;
  nameLines?.forEach((line) => { doc?.text(line, marginX, y); y += 17; });

  doc?.setFontSize(9.5);
  doc?.setTextColor(120, 113, 108);
  doc?.setFont(undefined, 'normal');
  doc?.text(card?.plantName || '—', marginX, y);
  y += 14;

  doc?.setFontSize(9);
  doc?.setTextColor(180, 83, 9);
  doc?.setFont(undefined, 'bold');
  doc?.text(`${card?.state || '—'}   ·   ${card?.district || '—'}   ·   Code ${card?.millCode || '—'}`, marginX, y);
  y += 20;

  const idFields = [
    ['Address', card?.address || '—'],
    ...(card?.website ? [['Website', card?.website]] : []),
    ['Phone', card?.phone || '—'],
    ['Email', card?.email || '—'],
  ];
  idFields?.forEach(([label, value]) => {
    doc?.setFontSize(7.5);
    doc?.setTextColor(120, 113, 108);
    doc?.setFont(undefined, 'bold');
    doc?.text(label?.toUpperCase(), marginX, y);
    y += 11;
    doc?.setFontSize(9.5);
    doc?.setTextColor(17, 17, 17);
    doc?.setFont(undefined, 'normal');
    const lines = doc?.splitTextToSize(String(value), contentW);
    lines?.forEach((line) => { doc?.text(line, marginX, y); y += 12.5; });
    y += 4;
  });

  if (managers?.length > 0) {
    doc?.setFontSize(7.5);
    doc?.setTextColor(120, 113, 108);
    doc?.setFont(undefined, 'bold');
    doc?.text('MANAGERS / CONTACTS', marginX, y);
    y += 12;
    managers?.forEach((m) => {
      doc?.setFontSize(9.5);
      doc?.setTextColor(17, 17, 17);
      doc?.setFont(undefined, 'bold');
      doc?.text(m?.managerName || '—', marginX, y);
      doc?.setFont(undefined, 'normal');
      doc?.setTextColor(120, 113, 108);
      const contactBits = [m?.designation, m?.mobile || m?.phone, m?.email]?.filter(Boolean)?.join('  ·  ');
      if (contactBits) {
        const w1 = doc?.getTextWidth(`${m?.managerName || '—'}   `);
        doc?.setFontSize(8.5);
        doc?.text(contactBits, marginX + w1, y);
      }
      y += 14;
    });
    y += 4;
  }

  // ── Market Intelligence ──
  y += 6;
  const miBoxY = y;

  // Pre-compute box height (pure arithmetic mirroring the content layout below) so the box can be
  // drawn as a background BEFORE its content. jsPDF has no z-order — drawing the fill after the
  // text/chart/badges (as before) painted over and erased them, leaving a blank filled rectangle.
  let chartBottomYCalc = y + 68;
  if (chartImg) {
    const imgWCalc = contentW;
    const imgHCalc = Math.min(imgWCalc / chartAspect, 90);
    chartBottomYCalc = y + 66 + imgHCalc;
  }
  const badgeYCalc = chartBottomYCalc + 14;
  const miBoxH = (badgeYCalc + 12) - miBoxY + 10;

  doc?.setDrawColor(253, 230, 138);
  doc?.setFillColor(255, 251, 235);
  doc?.roundedRect(marginX - 8, miBoxY - 10, contentW + 16, miBoxH, 6, 6, 'FD');

  doc?.setFontSize(8);
  doc?.setTextColor(146, 64, 14);
  doc?.setFont(undefined, 'bold');
  doc?.text('MARKET INTELLIGENCE', marginX, y + 16);

  const monthLabel = formatMonthPDF(card?.miData?.quotaPeriodMonth);
  doc?.setFontSize(9.5);
  doc?.setTextColor(120, 87, 20);
  doc?.setFont(undefined, 'normal');
  doc?.text(monthLabel ? `${monthLabel} Quota` : "This Month's Release Quota", marginX, y + 34);

  doc?.setFontSize(20);
  doc?.setTextColor(180, 83, 9);
  doc?.setFont(undefined, 'bold');
  doc?.text(`${fmtNum(card?.miData?.thisMonthQuota || 0)} MT`, marginX, y + 56);

  let chartBottomY = y + 68;
  if (chartImg) {
    const imgW = contentW;
    const imgH = Math.min(imgW / chartAspect, 90);
    doc?.addImage(chartImg, 'PNG', marginX, y + 66, imgW, imgH);
    chartBottomY = y + 66 + imgH;
  }

  y = chartBottomY + 14;
  const badgeY = y;
  doc?.setFontSize(8.5);
  doc?.setFont(undefined, 'bold');
  doc?.setTextColor(146, 64, 14);
  doc?.text(card?.miData?.stateRank ? `State Rank #${card?.miData?.stateRank}` : 'State Rank — Not Ranked', marginX, badgeY);
  doc?.setTextColor(194, 65, 12);
  doc?.text(
    card?.miData?.nationalRank ? `National Rank #${card?.miData?.nationalRank}` : 'National Rank — Not Ranked',
    marginX + 160, badgeY
  );
  const yoyPositive = card?.miData?.yoyPositive;
  doc?.setTextColor(yoyPositive ? 22 : 220, yoyPositive ? 163 : 20, yoyPositive ? 74 : 60);
  const yoyText = card?.miData?.yoyChange != null ? `${yoyPositive ? '+' : ''}${card?.miData?.yoyChange}% YoY` : 'YoY — N/A';
  doc?.text(yoyText, marginX + 330, badgeY);
  y = badgeY + 12;
  y += 16;

  // ── Current Live Rate ──
  doc?.setFontSize(9);
  doc?.setTextColor(146, 64, 14);
  doc?.setFont(undefined, 'bold');
  doc?.text('CURRENT LIVE RATE', marginX, y);
  y += 14;

  if (!liveRates || liveRates?.length === 0) {
    doc?.setFontSize(9);
    doc?.setTextColor(168, 162, 158);
    doc?.setFont(undefined, 'normal');
    doc?.text('No live rate currently on record for this mill.', marginX, y);
    y += 16;
  } else {
    liveRates?.forEach((r) => {
      doc?.setFillColor(248, 241, 233);
      doc?.setDrawColor(232, 221, 208);
      doc?.roundedRect(marginX, y - 10, contentW, 46, 5, 5, 'FD');

      doc?.setFontSize(10.5);
      doc?.setTextColor(17, 17, 17);
      doc?.setFont(undefined, 'bold');
      doc?.text(r?.grade || '—', marginX + 10, y + 4);
      doc?.setFontSize(8);
      doc?.setTextColor(22, 163, 74);
      doc?.text('LIVE', marginX + contentW - 30, y + 4);

      const cols = [
        ['Rate Excl GST', `₹${fmtNum(r?.rate_excl_gst || 0)}`],
        ['Rate Incl GST', `₹${fmtNum(r?.rate_incl_gst || 0)}`],
        ['Payment Date', formatDatePDF(r?.payment_date)],
        ['Lifting Date', formatDatePDF(r?.lifting_date)],
      ];
      const colW = (contentW - 20) / 4;
      cols?.forEach(([label, value], i) => {
        const x = marginX + 10 + i * colW;
        doc?.setFontSize(6.5);
        doc?.setTextColor(120, 113, 108);
        doc?.setFont(undefined, 'normal');
        doc?.text(label?.toUpperCase(), x, y + 18);
        doc?.setFontSize(8.5);
        doc?.setTextColor(17, 17, 17);
        doc?.setFont(undefined, 'bold');
        doc?.text(String(value), x, y + 30);
      });

      y += 54;
    });
  }

  const { distances: nearbyMills } = await fetchDistrictMillDistances(card?.id);
  y = drawNearbyMillsDistance(doc, w, h, marginX, contentW, y + 10, nearbyMills, card?.name);

  if (y > h - CTA_BLOCK_HEIGHT - FOOTER_RESERVED_HEIGHT) {
    doc?.addPage();
    drawWatermark(doc, w, h);
    y = 40;
  }
  y = drawCTAButtons(doc, marginX, contentW, y + 10);

  const disclosure = [
    'Rate and quota figures reflect the latest data on record with SugarHathi at the time this was',
    'generated — always confirm current figures directly with the mill before acting on them.',
  ];
  drawFooter(doc, w, h, disclosure);

  const fileName = `SugarHathi_${shortName(card?.name)}_Rate.pdf`;
  doc?.save(fileName);
}
